import { ChangeDetectionStrategy, Component, Input, OnChanges, OnInit, SimpleChanges } from "@angular/core";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { FacetValueIdPair, FacetWithValues } from "../../providers/facet-filter.service";
import { PriceRangeInputsComponent } from "../price-range-inputs/price-range-inputs.component";
import { CheckboxComponent } from "../../../shared/components/checkbox/checkbox.component";
import { NgFor, AsyncPipe, TitleCasePipe } from "@angular/common";

@Component({
    selector: 'vsf-product-list-filters',
    templateUrl: './product-list-filters.component.html',
    styleUrls: ['./product-list-filters.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        NgFor,
        RouterLink,
        CheckboxComponent,
        PriceRangeInputsComponent,
        AsyncPipe,
        TitleCasePipe,
    ],
})
export class ProductListFiltersComponent implements OnInit, OnChanges {
    @Input() activeFacetValueIds: FacetValueIdPair[] = [];
    @Input() facets: FacetWithValues[] = [];
    @Input() featureFacetIds: string[] = [];
    @Input() excludeFacetIds: string[] = [];
    @Input() collectionSlug: string;
    min$: Observable<number | undefined>;
    max$: Observable<number | undefined>;
    expandAll = false;
    expandedFacets = new Map<string, boolean>();
    displayableFacets: FacetWithValues[];
    private readonly expandAllIfFewerThanFacets = 5;

    constructor(private route: ActivatedRoute, private router: Router) {}

    ngOnInit() {
        this.min$ = this.route.queryParamMap.pipe(
            map((pm) => pm.get('min')),
            map((min) => (min != null ? +min : undefined)),
        );
        this.max$ = this.route.queryParamMap.pipe(
            map((pm) => pm.get('max')),
            map((max) => (max != null ? +max : undefined)),
        );
    }

    ngOnChanges(changes: SimpleChanges) {
        if ('facets' in changes || 'excludeFacetIds' in changes || 'featureFacetIds' in changes) {
            this.expandAll = this.facets?.length < this.expandAllIfFewerThanFacets;
            const displayableFacets = this.facets?.filter((f) => !this.excludeFacetIds?.includes(f.id)) ?? [];
            this.displayableFacets = displayableFacets.sort((a, b) => {
                return this.featureFacetIds?.includes(a.id) ? -1 : 1;
            });
        }
        if ('collectionSlug' in changes) {
            this.expandedFacets.clear();
        }
    }

    toggleExpanded(facetValueId: string) {
        const explicitState = this.expandedFacets.get(facetValueId);
        const current = typeof explicitState === 'boolean' ? explicitState : this.expandAll;
        this.expandedFacets.set(facetValueId, !current);
    }

    isExpanded(facet: FacetWithValues): boolean {
        if (this.expandedFacets.get(facet.id) === false) {
            return false;
        }
        const isFeatured = this.featureFacetIds?.includes(facet.id);
        return isFeatured || this.expandAll || facet.isActive || this.expandedFacets.get(facet.id) === true;
    }

    toggleFacetValueId(facetId: string, valueId: string): string[] {
        const pair = `${facetId}-${valueId}` as const;
        const existing = this.activeFacetValueIds;
        return existing.includes(pair) ? existing.filter((x) => x !== pair) : existing.concat(pair);
    }

    trackById(index: number, item: { id: string }) {
        return item.id;
    }

    setPriceRange(range: { min?: number; max?: number }) {
        const params: any = {};
        if (range.min != null) {
            params.min = range.min;
        }
        if (range.max != null) {
            params.max = range.max;
        }
        this.router.navigate(['./'], {
            queryParams: params,
            queryParamsHandling: 'merge',
            relativeTo: this.route,
        });
    }
}
